import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Card,
  Typography,
  Stack,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  FolderOpen as FolderOpenIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  ReportProblemOutlined as MissingIcon,
} from '@mui/icons-material';
import apiService, { authFetch } from '../../services/apiServices';
import Toast from '../actions/Toasts';

// Map a backend worker_files row to the shape this component renders.
const mapServerFile = (f) => ({
  id: f.id,
  title: f.title,
  category: f.category || 'document',
  description: f.description || '',
  uploadDate: f.created_at ? String(f.created_at).slice(0, 10) : '',
  size: f.size_bytes || 0,
  fileName: f.original_name || '',
  uploadedBy: f.uploaded_by_name || '',
  // Regjistrimi ekziston, skedari në server jo — rreshti ofron vetëm "Ngarko sërish".
  missing: !!f.file_missing,
});

export default function WorkerFiles() {
  const { t } = useTranslation();
  const FILE_CATEGORIES = [
    { value: 'contract', label: t('workerFiles.contract'), color: 'primary', icon: <DescriptionIcon /> },
    { value: 'leave', label: t('workerFiles.leave'), color: 'success', icon: <DescriptionIcon /> },
    { value: 'warning', label: t('worker.directory.warnings'), color: 'error', icon: <DescriptionIcon /> },
    { value: 'document', label: t('workerFiles.document'), color: 'info', icon: <FileIcon /> },
    { value: 'certificate', label: t('workerFiles.certificate'), color: 'secondary', icon: <DescriptionIcon /> },
    { value: 'other', label: t('workerFiles.other'), color: 'default', icon: <FileIcon /> },
  ];
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  // Shikimi brenda aplikacionit: { file, url, type } — blob-i lirohet kur mbyllet.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [newFile, setNewFile] = useState({
    title: '',
    category: 'document',
    description: '',
    file: null,
  });
  const [toast, setToast] = useState({ open: false, message: '', variant: 'success' });
  const [uploading, setUploading] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [mobileView, setMobileView] = useState('workers'); // 'workers' | 'files'

  const normalizeWorkerRow = useCallback((row = {}) => ({
    ...row,
    id: row?.Id ?? row?.id,
    personalId: row?.PersonalId ?? row?.personal_id ?? row?.personalid,
    firstName: row?.FirstName ?? row?.first_name ?? row?.firstname,
    lastName: row?.LastName ?? row?.last_name ?? row?.lastname,
    email: row?.Email ?? row?.email,
    phone: row?.Phone ?? row?.phone,
    place: row?.Place ?? row?.place,
    department: row?.Department ?? row?.department,
    name: `${row?.FirstName || row?.first_name || ''} ${row?.LastName || row?.last_name || ''}`.trim(),
  }), []);

  // "Ngarko sërish": një input i fshehur për të gjithë rreshtat, me skedarin
  // që po zëvendësohet në ref.
  const reuploadInputRef = useRef(null);
  const reuploadTargetRef = useRef(null);
  const [reuploadingId, setReuploadingId] = useState(null);

  // AbortController ref so switching workers cancels the previous in-flight request.
  const filesAbortRef = useRef(null);

  const loadWorkerFiles = useCallback(async (workerId) => {
    if (!workerId) { setFiles([]); return; }
    // Cancel any previous in-flight request for files.
    if (filesAbortRef.current) {
      filesAbortRef.current.abort();
    }
    const controller = new AbortController();
    filesAbortRef.current = controller;
    try {
      const res = await authFetch(`/api/workers/${workerId}/files`, { signal: controller.signal })
        .then(r => r.json());
      if (!controller.signal.aborted) {
        setFiles(res?.success && Array.isArray(res.data) ? res.data.map(mapServerFile) : []);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      setFiles([]);
      setToast({ open: true, message: t('workerFiles.loadFilesError'), variant: 'error' });
    }
  }, [t]);

  const loadWorkers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService().get('workers');
      if (res?.data && Array.isArray(res.data)) {
        const normalized = res.data.map(normalizeWorkerRow);
        setWorkers(normalized);
        if (normalized.length > 0) {
          setSelectedWorker(normalized[0]);
        }
      }
    } catch (error) {
      setToast({ open: true, message: t('workerFiles.loadWorkersError'), variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t, normalizeWorkerRow]);

  useEffect(() => {
    loadWorkers();
  }, [loadWorkers]);

  useEffect(() => {
    if (selectedWorker) {
      loadWorkerFiles(selectedWorker.id);
    }
  }, [selectedWorker, loadWorkerFiles]);

  const handleWorkerSelect = (worker) => {
    setSelectedWorker(worker);
  };

  const handleFileMenuOpen = (event, file) => {
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleFileMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
    setNewFile({
      title: '',
      category: 'document',
      description: '',
      file: null,
    });
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
  };

  const handleFileUpload = async () => {
    if (!newFile.title || !newFile.file) {
      setToast({ open: true, message: t('workerFiles.fillRequired'), variant: 'warning' });
      return;
    }
    if (!selectedWorker?.id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document', newFile.file);
      fd.append('title', newFile.title);
      fd.append('category', newFile.category || 'document');
      fd.append('description', newFile.description || '');
      const res = await authFetch(`/api/workers/${selectedWorker.id}/files`, { method: 'POST', body: fd })
        .then(r => r.json());
      if (res?.success && res.data) {
        setFiles(prevFiles => [mapServerFile(res.data), ...prevFiles]);
        setToast({ open: true, message: t('workerFiles.fileUploaded'), variant: 'success' });
        handleUploadDialogClose();
      } else {
        setToast({ open: true, message: res?.error || t('workerFiles.loadFilesError'), variant: 'error' });
      }
    } catch (error) {
      setToast({ open: true, message: t('workerFiles.loadFilesError'), variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const markMissing = (fileId) =>
    setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, missing: true } : f)));

  const startReupload = (file) => {
    reuploadTargetRef.current = file;
    if (reuploadInputRef.current) {
      reuploadInputRef.current.value = '';
      reuploadInputRef.current.click();
    }
  };

  const handleReupload = async (picked) => {
    const target = reuploadTargetRef.current;
    if (!picked || !target || !selectedWorker?.id) return;
    setReuploadingId(target.id);
    try {
      const fd = new FormData();
      fd.append('document', picked);
      const res = await authFetch(`/api/workers/${selectedWorker.id}/files/${target.id}/replace`, { method: 'POST', body: fd })
        .then((r) => r.json());
      if (res?.success && res.data) {
        setFiles((prev) => prev.map((f) => (f.id === target.id ? mapServerFile(res.data) : f)));
        setToast({ open: true, message: t('workerFiles.reuploaded'), variant: 'success' });
      } else {
        setToast({ open: true, message: res?.error || t('workerFiles.loadFilesError'), variant: 'error' });
      }
    } catch (error) {
      setToast({ open: true, message: t('workerFiles.loadFilesError'), variant: 'error' });
    } finally {
      setReuploadingId(null);
      reuploadTargetRef.current = null;
      handleFileMenuClose();
    }
  };

  const handleFileDelete = async () => {
    if (!selectedFile || !selectedWorker?.id) return;
    const fileId = selectedFile.id;
    try {
      const res = await authFetch(`/api/workers/${selectedWorker.id}/files/${fileId}`, { method: 'DELETE' })
        .then(r => r.json());
      if (res?.success) {
        setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
        setToast({ open: true, message: t('workerFiles.fileDeleted'), variant: 'success' });
      } else {
        setToast({ open: true, message: res?.error || t('workerFiles.loadFilesError'), variant: 'error' });
      }
    } catch (error) {
      setToast({ open: true, message: t('workerFiles.loadFilesError'), variant: 'error' });
    } finally {
      handleFileMenuClose();
    }
  };

  /**
   * Shiko pa shkarkuar. PDF, imazhet dhe teksti hapen brenda aplikacionit;
   * formatet që shfletuesi s'i vizaton (Word, Excel) tregojnë shkarkimin.
   */
  const handleFileView = async (file) => {
    if (!file || !selectedWorker?.id) return;
    if (file.missing) { startReupload(file); return; }
    setPreviewLoading(true);
    setPreview({ file, url: null, type: '' });
    try {
      const res = await authFetch(`/api/workers/${selectedWorker.id}/files/${file.id}/download`);
      if (res.status === 404) {
        setPreview(null);
        markMissing(file.id);
        setToast({ open: true, message: t('workerFiles.fileMissingToast'), variant: 'warning' });
        return;
      }
      if (!res.ok) throw new Error('view failed');
      const blob = await res.blob();
      setPreview({ file, url: URL.createObjectURL(blob), type: blob.type || '' });
    } catch (error) {
      setPreview(null);
      setToast({ open: true, message: t('workerFiles.loadFilesError'), variant: 'error' });
    } finally {
      setPreviewLoading(false);
      handleFileMenuClose();
    }
  };
  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };
  const previewKind = (() => {
    const tp = preview?.type || '';
    const name = (preview?.file?.fileName || '').toLowerCase();
    if (tp.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return 'image';
    if (tp === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    if (tp.startsWith('text/') || /\.(txt|csv)$/.test(name)) return 'text';
    return 'other';
  })();

  const handleFileDownload = async (file) => {
    if (!file || !selectedWorker?.id) return;
    if (file.missing) { startReupload(file); return; }
    try {
      const res = await authFetch(`/api/workers/${selectedWorker.id}/files/${file.id}/download`);
      if (res.status === 404) {
        markMissing(file.id);
        setToast({ open: true, message: t('workerFiles.fileMissingToast'), variant: 'warning' });
        return;
      }
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToast({ open: true, message: t('workerFiles.downloading', { name: file.fileName }), variant: 'info' });
    } catch (error) {
      setToast({ open: true, message: t('workerFiles.loadFilesError'), variant: 'error' });
    } finally {
      handleFileMenuClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Modern file-type system: soft tinted container + matching icon
  const getFileMeta = (fileName) => {
    const ext = (fileName || '').split('.').pop()?.toLowerCase();
    if (ext === 'pdf')
      return { Icon: PdfIcon,  bg: '#FEF2F2', fg: '#DC2626', label: 'PDF' };
    if (['doc', 'docx'].includes(ext))
      return { Icon: DescriptionIcon, bg: '#EFF6FF', fg: '#2563EB', label: 'DOC' };
    if (['xls', 'xlsx', 'csv'].includes(ext))
      return { Icon: FileIcon, bg: '#ECFDF5', fg: '#059669', label: 'XLS' };
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))
      return { Icon: ImageIcon, bg: '#F5F3FF', fg: '#7C3AED', label: ext?.toUpperCase() };
    return { Icon: FileIcon, bg: '#F8FAFC', fg: '#64748B', label: ext?.toUpperCase() || 'FILE' };
  };

  const filteredWorkers = workers.filter((worker) =>
    (worker.name || `${worker.firstName || ''} ${worker.lastName || ''}`.trim())
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const filteredFiles = categoryFilter === 'all'
    ? files
    : files.filter(f => f.category === categoryFilter);

  const getCategoryInfo = (category) => {
    return FILE_CATEGORIES.find((c) => c.value === category) || FILE_CATEGORIES[0];
  };

  // Skip raw numeric IDs that show up where a human-readable label is expected.
  const isHumanLabel = (v) => v && !/^\d+$/.test(String(v).trim());
  const workerSubtitle = (w) => (isHumanLabel(w?.department) && w.department) || w?.email || w?.phone || '';

  // Group files by month for better scanning when the list grows.
  const monthLabelFor = (dateStr) => {
    if (!dateStr) return t('workerFiles.noDate');
    const d = new Date(dateStr);
    if (isNaN(d)) return t('workerFiles.noDate');
    const now = new Date();
    const sameYear = d.getFullYear() === now.getFullYear();
    const monthName = d.toLocaleDateString('sq-AL', { month: 'long' });
    return sameYear ? monthName.charAt(0).toUpperCase() + monthName.slice(1) : `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${d.getFullYear()}`;
  };
  const groupedFiles = (() => {
    const sorted = [...filteredFiles].sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
    const groups = new Map();
    sorted.forEach(f => {
      const key = monthLabelFor(f.uploadDate);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(f);
    });
    return Array.from(groups.entries()); // [[label, files[]]]
  })();

  // Aggregate file size for the header summary.
  const totalSize = filteredFiles.reduce((s, f) => s + (f.size || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', bgcolor: 'background.default' }}>
      <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ flex: 1, minHeight: 0, height: '100%' }}>

        {/* ── Employee sidebar ────────────────────────────────────────────── */}
        <Grid
          item xs={12} md={3.5} lg={3}
          sx={{
            display: { xs: isMobile && mobileView === 'files' ? 'none' : 'flex', md: 'flex' },
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Card
            elevation={0}
            sx={{
              flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
              border: 1, borderColor: 'divider', borderRadius: 2,
            }}
          >
            {/* Sidebar header: title + employee count + search */}
            <Box sx={{ p: 2, pb: 1.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.005em' }}>
                  {t('workerFiles.employeesTitle')}
                </Typography>
                <Chip
                  label={loading ? '…' : workers.length}
                  size="small"
                  sx={{ height: 20, fontWeight: 600, fontSize: '0.6875rem', bgcolor: 'grey.100', color: 'text.secondary', border: 0, minWidth: 28 }}
                />
              </Stack>
              <TextField
                fullWidth
                size="small"
                placeholder={t('worker.directory.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    minHeight: 40,
                    bgcolor: 'background.paper',
                    fontSize: '0.875rem',
                  },
                  '& .MuiOutlinedInput-input': { py: '8px' },
                }}
              />
            </Box>

            {/* List */}
            <Box sx={{ overflowY: 'auto', flex: 1, px: 1, pb: 1 }}>
              {filteredWorkers.map((worker) => {
                const isSelected = selectedWorker?.id === worker.id;
                const initials = (worker.name || `${worker.firstName || ''} ${worker.lastName || ''}`)
                  .trim().split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'P';
                return (
                  <Box
                    key={worker.id}
                    onClick={() => { handleWorkerSelect(worker); if (isMobile) setMobileView('files'); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 1.25, py: 1.25, mb: 0.25,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'primary.soft' : 'transparent',
                      transition: 'background-color 0.12s ease',
                      '&:hover': { bgcolor: isSelected ? 'primary.soft' : 'grey.50' },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 34, height: 34,
                        fontSize: '0.8125rem', fontWeight: 600,
                        bgcolor: isSelected ? 'primary.main' : 'primary.soft',
                        color: isSelected ? 'primary.contrastText' : 'primary.main',
                        transition: 'background-color 0.15s ease, color 0.15s ease',
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        noWrap
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: isSelected ? 'primary.dark' : 'text.primary',
                          letterSpacing: '-0.005em',
                          lineHeight: 1.35,
                        }}
                      >
                        {worker.name || t('workerFiles.noName')}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          fontWeight: 500,
                          mt: 0.25,
                        }}
                      >
                        {workerSubtitle(worker)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              {filteredWorkers.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 32, color: 'grey.300', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                    {t('workerFiles.noWorkers')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* ── Document workspace ──────────────────────────────────────────── */}
        <Grid
          item xs={12} md={8.5} lg={9}
          sx={{
            display: { xs: isMobile && mobileView === 'workers' ? 'none' : 'flex', md: 'flex' },
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {selectedWorker ? (
            <Card
              elevation={0}
              sx={{
                flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
                border: 1, borderColor: 'divider', borderRadius: 2,
              }}
            >
              {/* Header: employee summary + upload */}
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={2}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    {isMobile && (
                      <IconButton
                        onClick={() => setMobileView('workers')}
                        sx={{ width: 36, height: 36, border: 1, borderColor: 'divider', borderRadius: 1.5, flexShrink: 0 }}
                      >
                        <ArrowBackIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                    <Avatar
                      sx={{
                        width: 44, height: 44,
                        fontSize: '0.9375rem', fontWeight: 600,
                        bgcolor: 'primary.soft', color: 'primary.main',
                      }}
                    >
                      {(selectedWorker.name || 'P').trim().split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        noWrap
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.125rem' },
                          fontWeight: 600,
                          color: 'text.primary',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.3,
                        }}
                      >
                        {selectedWorker.name || t('workerFiles.noName')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500, mt: 0.25 }}>
                        {filteredFiles.length} {t('workerFiles.files')}
                        {totalSize > 0 && <> · {formatFileSize(totalSize)}</>}
                        {isHumanLabel(selectedWorker.department) && <> · {selectedWorker.department}</>}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={handleUploadDialogOpen}
                    sx={{ height: 40, borderRadius: 1.5, px: 2, flexShrink: 0 }}
                  >
                    {t('workerFiles.upload')} {t('workerFiles.file')}
                  </Button>
                </Stack>
              </Box>

              {/* Filter pills */}
              <Box
                sx={{
                  px: { xs: 2, sm: 3 }, pb: 2,
                  display: 'flex', gap: 0.75, flexWrap: 'wrap',
                  borderBottom: 1, borderColor: 'divider',
                }}
              >
                {[{ value: 'all', label: t('workerFiles.all') }, ...FILE_CATEGORIES.map(c => ({ value: c.value, label: c.label }))].map(tab => {
                  const isActive = categoryFilter === tab.value;
                  return (
                    <Box
                      key={tab.value}
                      onClick={() => setCategoryFilter(tab.value)}
                      sx={{
                        px: 1.5, py: 0.75,
                        borderRadius: 1.25,
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: isActive ? 'primary.soft' : 'transparent',
                        color: isActive ? 'primary.dark' : 'text.secondary',
                        transition: 'background-color 0.12s ease, color 0.12s ease',
                        '&:hover': { bgcolor: isActive ? 'primary.soft' : 'grey.100', color: isActive ? 'primary.dark' : 'text.primary' },
                      }}
                    >
                      {tab.label}
                    </Box>
                  );
                })}
              </Box>

              {/* File list — grouped by month for easier scanning */}
              <Box sx={{ overflowY: 'auto', flex: 1, px: { xs: 1, sm: 1.5 }, py: 1.5 }}>
                {filteredFiles.length > 0 ? (
                  <Box sx={{ maxWidth: 880, mx: 'auto' }}>
                    {groupedFiles.map(([groupLabel, items], gIdx) => (
                      <Box key={groupLabel} sx={{ mb: gIdx === groupedFiles.length - 1 ? 0 : 2.5 }}>
                        {/* Group header */}
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ px: { xs: 1.25, sm: 1.75 }, mb: 0.5 }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              color: 'text.disabled',
                            }}
                          >
                            {groupLabel}
                          </Typography>
                          <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'text.disabled' }}>
                            {items.length}
                          </Typography>
                        </Stack>

                        {/* Files in group */}
                        {items.map((file) => {
                          const categoryInfo = getCategoryInfo(file.category);
                          const fileMeta = getFileMeta(file.fileName);
                          const FileTypeIcon = fileMeta.Icon;
                          return (
                            <Box
                              key={file.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 1.5, sm: 2 },
                                px: { xs: 1.25, sm: 1.75 },
                                py: 1.5,
                                borderRadius: 1.5,
                                transition: 'background-color 0.15s ease',
                                '&:hover': {
                                  bgcolor: 'grey.50',
                                  '& .file-actions': { opacity: 1 },
                                },
                              }}
                            >
                              {/* File type icon */}
                              <Box
                                sx={{
                                  width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  bgcolor: file.missing ? '#FFF4E5' : fileMeta.bg,
                                  color: file.missing ? 'warning.main' : fileMeta.fg,
                                }}
                              >
                                {file.missing ? <MissingIcon sx={{ fontSize: 20 }} /> : <FileTypeIcon sx={{ fontSize: 20 }} />}
                              </Box>

                              {/* Name + metadata */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  noWrap
                                  onClick={() => handleFileView(file)}
                                  title={t('workerFiles.view', 'Shiko')}
                                  sx={{
                                    fontSize: '0.9375rem',
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    letterSpacing: '-0.005em',
                                    lineHeight: 1.35,
                                    cursor: 'pointer',
                                    '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                                  }}
                                >
                                  {file.title}
                                </Typography>
                                {file.missing && (
                                  <Typography sx={{ fontSize: '0.78rem', color: 'warning.dark', fontWeight: 600, mt: 0.25 }}>
                                    {t('workerFiles.fileMissing')} · <Box component="span" sx={{ fontWeight: 400 }}>{t('workerFiles.fileMissingHint')}</Box>
                                  </Typography>
                                )}
                                <Stack
                                  direction="row"
                                  spacing={1.25}
                                  divider={<Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'grey.300', alignSelf: 'center' }} />}
                                  sx={{ mt: 0.5, flexWrap: 'wrap' }}
                                >
                                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {file.uploadDate}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {formatFileSize(file.size)}
                                  </Typography>
                                  {file.uploadedBy && (
                                    <Typography
                                      sx={{ fontSize: '0.75rem', color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}
                                    >
                                      {file.uploadedBy}
                                    </Typography>
                                  )}
                                </Stack>
                              </Box>

                              {/* Category badge */}
                              <Chip
                                label={categoryInfo.label}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontWeight: 500,
                                  bgcolor: 'grey.100',
                                  color: 'text.secondary',
                                  border: 0,
                                  display: { xs: 'none', md: 'inline-flex' },
                                }}
                              />

                              {/* Actions */}
                              <Stack
                                className="file-actions"
                                direction="row"
                                spacing={0.25}
                                sx={{
                                  flexShrink: 0,
                                  // Rreshti që kërkon veprim e tregon butonin gjithmonë, jo vetëm në hover.
                                  opacity: { xs: 1, md: file.missing ? 1 : 0 },
                                  transition: 'opacity 0.15s ease',
                                }}
                              >
                                {file.missing ? (
                                  <Button
                                    size="small" variant="contained" color="warning" disableElevation
                                    startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                                    disabled={reuploadingId === file.id}
                                    onClick={() => startReupload(file)}
                                    sx={{ height: 32, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                                  >
                                    {reuploadingId === file.id ? '…' : t('workerFiles.reupload')}
                                  </Button>
                                ) : (<>
                                <Tooltip title={t('workerFiles.view', 'Shiko')}>
                                  <IconButton
                                    onClick={() => handleFileView(file)}
                                    sx={{ width: 32, height: 32 }}
                                  >
                                    <VisibilityIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('workerFiles.download')}>
                                  <IconButton
                                    onClick={() => handleFileDownload(file)}
                                    sx={{ width: 32, height: 32 }}
                                  >
                                    <DownloadIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Tooltip>
                                </>)}
                                <IconButton
                                  onClick={(e) => handleFileMenuOpen(e, file)}
                                  sx={{ width: 32, height: 32 }}
                                >
                                  <MoreVertIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 56, height: 56, borderRadius: 2,
                        bgcolor: 'grey.100',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        mb: 1.5,
                      }}
                    >
                      <FolderOpenIcon sx={{ fontSize: 26, color: 'text.disabled' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                      {t('workerFiles.noFilesUploaded')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      {t('workerFiles.noFilesHint')}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon sx={{ fontSize: '16px !important' }} />}
                      onClick={handleUploadDialogOpen}
                      sx={{ height: 36, borderRadius: 1.5 }}
                    >
                      {t('workerFiles.upload')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Card>
          ) : (
            <Card
              elevation={0}
              sx={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 1, borderColor: 'divider', borderRadius: 2,
              }}
            >
              <Box sx={{ textAlign: 'center', px: 3 }}>
                <Box
                  sx={{
                    width: 56, height: 56, borderRadius: 2,
                    bgcolor: 'primary.soft', color: 'primary.main',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 26 }} />
                </Box>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  {t('workerFiles.selectWorker')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('workerFiles.selectWorkerHint')}
                </Typography>
              </Box>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleUploadDialogClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: '1.0625rem', fontWeight: 600 }}>{t('workerFiles.uploadNewFile')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Modern drag-and-drop file zone */}
            <Box
              component="label"
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 1,
                p: 3, borderRadius: 2,
                border: '2px dashed',
                borderColor: newFile.file ? 'primary.main' : 'divider',
                bgcolor: newFile.file ? 'primary.soft' : 'grey.50',
                cursor: 'pointer',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.soft' },
              }}
            >
              {newFile.file ? (
                <>
                  {(() => {
                    const m = getFileMeta(newFile.file.name);
                    const I = m.Icon;
                    return (
                      <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: m.bg, color: m.fg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <I sx={{ fontSize: 22 }} />
                      </Box>
                    );
                  })()}
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>
                    {newFile.file.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {formatFileSize(newFile.file.size)} · {t('workerFiles.selectFile')}
                  </Typography>
                </>
              ) : (
                <>
                  <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: 'background.paper', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CloudUploadIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>
                    {t('workerFiles.selectFile')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {t('workerFiles.fileTypes')}
                  </Typography>
                </>
              )}
              <input
                type="file"
                hidden
                onChange={(e) => setNewFile({ ...newFile, file: e.target.files[0] })}
              />
            </Box>

            <TextField
              fullWidth
              label={t('workerFiles.title')}
              required
              value={newFile.title}
              onChange={(e) => setNewFile({ ...newFile, title: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>{t('workerFiles.category')}</InputLabel>
              <Select
                value={newFile.category}
                label={t('workerFiles.category')}
                onChange={(e) => setNewFile({ ...newFile, category: e.target.value })}
              >
                {FILE_CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={t('workerFiles.description')}
              multiline
              rows={2}
              value={newFile.description}
              onChange={(e) => setNewFile({ ...newFile, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleUploadDialogClose} sx={{ height: 40 }}>{t('common.cancel')}</Button>
          <Button onClick={handleFileUpload} variant="contained" disabled={uploading} sx={{ height: 40 }}>
            {t('workerFiles.upload')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* "Ngarko sërish" — zgjedhja e skedarit për rreshtin që mungon */}
      <input
        ref={reuploadInputRef}
        type="file"
        hidden
        onChange={(e) => handleReupload(e.target.files?.[0])}
      />

      {/* File Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleFileMenuClose}
      >
        {selectedFile?.missing ? (
          <MenuItem onClick={() => { const f = selectedFile; handleFileMenuClose(); startReupload(f); }}>
            <CloudUploadIcon fontSize="small" sx={{ mr: 1.25 }} />
            {t('workerFiles.reupload')}
          </MenuItem>
        ) : [
          <MenuItem key="view" onClick={() => selectedFile && handleFileView(selectedFile)}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1.25 }} />
            {t('workerFiles.view', 'Shiko')}
          </MenuItem>,
          <MenuItem key="download" onClick={() => selectedFile && handleFileDownload(selectedFile)}>
            <DownloadIcon fontSize="small" sx={{ mr: 1.25 }} />
            {t('workerFiles.download')}
          </MenuItem>,
        ]}
        <Divider />
        <MenuItem onClick={handleFileDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1.25 }} />
          {t('common.delete')}
        </MenuItem>
      </Menu>

      {/* Shikuesi i dokumentit */}
      <Dialog open={Boolean(preview)} onClose={closePreview} maxWidth="lg" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { height: { md: '90vh' } } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25 }}>
          <Typography noWrap sx={{ flex: 1, fontWeight: 700 }}>{preview?.file?.title || preview?.file?.fileName}</Typography>
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => preview?.file && handleFileDownload(preview.file)}>
            {t('workerFiles.download')}
          </Button>
          <IconButton onClick={closePreview} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', minHeight: 300 }}>
          {previewLoading || !preview?.url ? (
            <Typography color="text.secondary">…</Typography>
          ) : previewKind === 'image' ? (
            <Box component="img" src={preview.url} alt={preview.file.title} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : previewKind === 'pdf' || previewKind === 'text' ? (
            <Box component="iframe" src={preview.url} title={preview.file.title} sx={{ width: '100%', height: '100%', minHeight: '70vh', border: 0, bgcolor: 'white' }} />
          ) : (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <FileIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography sx={{ mt: 1, fontWeight: 600 }}>Ky format nuk shfaqet dot në shfletues</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                PDF-të dhe fotot hapen këtu; dokumentet Word/Excel duhet të shkarkohen.
              </Typography>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => handleFileDownload(preview.file)}>
                {t('workerFiles.download')}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Toast {...toast} />
    </Box>
  );
}
